/**
 * SmileFy 3D Viewer
 *
 * Fixes applied to the reference code:
 *  - three/addons/ → three/examples/jsm/  (addons path absent in three@0.142)
 *  - outputColorSpace → outputEncoding     (outputColorSpace added in r152)
 *  - SRGBColorSpace   → sRGBEncoding       (correct enum for r142 outputEncoding)
 *  - TypeScript stripped to plain JS
 *  - Private _spherical hack removed
 *  - File input adapted to URL (fetch → ArrayBuffer) for S3 pre-signed URLs
 */
import * as React from "react";
import {useRef, useEffect, useState, useCallback} from "react";
import * as THREE from "three";
import {OrbitControls}   from "three/examples/jsm/controls/OrbitControls";
import {STLLoader}       from "three/examples/jsm/loaders/STLLoader";
import {OBJLoader}       from "three/examples/jsm/loaders/OBJLoader";
import {PLYLoader}       from "three/examples/jsm/loaders/PLYLoader";
import {mergeVertices}   from "three/examples/jsm/utils/BufferGeometryUtils";
import {RoomEnvironment} from "three/examples/jsm/environments/RoomEnvironment";
import styles from "./viewer.module.css";

/* ── Constants ── */
const SCENE_BG    = "#0d0e1e";
const MAT_PRESETS = {
  // Matches the webapp STLFullViewer "clay" look exactly: MeshPhongMaterial
  // (Blinn-Phong) — PBR softened the specular off the enamel ridges.
  tooth: { label: "Enamel", dot: "#d9d9d3",
    make: () => new THREE.MeshPhongMaterial({ color: 0xd9d9d3, specular: new THREE.Color(0x242424),
      shininess: 18, flatShading: false, side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1, transparent: false, opacity: 1 }) },
  bone: { label: "Bone", dot: "#ddd0b0",
    make: () => new THREE.MeshPhysicalMaterial({ color: 0xddd0b0, roughness: 0.75, metalness: 0,
      clearcoat: 0.05, side: THREE.DoubleSide, envMapIntensity: 1.0 }) },
  metal: { label: "Metal", dot: "#b8c8da",
    make: () => new THREE.MeshPhysicalMaterial({ color: 0xb8c8da, roughness: 0.18, metalness: 0.9,
      side: THREE.DoubleSide, envMapIntensity: 1.5 }) },
  xray: { label: "X-Ray", dot: "#22d3ee",
    make: () => new THREE.MeshPhysicalMaterial({ color: 0x22d3ee, roughness: 0.45, metalness: 0.05,
      transparent: true, opacity: 0.45, side: THREE.DoubleSide, envMapIntensity: 0.8 }) },
};

/* ── Build scene ── */
function buildScene(canvas) {
  const W = canvas.clientWidth  || canvas.offsetWidth  || 800;
  const H = canvas.clientHeight || canvas.offsetHeight || 600;

  /* Renderer */
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setSize(W, H, false);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // No tone mapping — matches the webapp/MeshLab look (ACES compresses the
  // crisp specular highlight off the enamel ridges). Linear out, sRGB transfer.
  renderer.toneMapping        = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputEncoding     = THREE.sRGBEncoding;   // ← correct for three@0.142
  renderer.shadowMap.enabled  = true;
  renderer.shadowMap.type     = THREE.PCFSoftShadowMap;

  /* Scene */
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SCENE_BG);

  /* PBR environment (RoomEnvironment gives excellent enamel shading) */
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.02).texture;
  pmrem.dispose();

  /* Lighting — MeshLab style, matches the webapp STLFullViewer: headlight-
     dominant. Hemi/key/fill are 0 by design; a camera-pinned headlight (added
     after the camera, below) + a faint rim do the shading. */
  scene.add(new THREE.HemisphereLight(0xffffff, 0x808080, 0));

  const key = new THREE.DirectionalLight(0xffffff, 0);
  key.position.set(-2, 4, 6);
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xffffff, 0);
  fill.position.set(5, 2, 4);
  scene.add(fill);

  const rim = new THREE.DirectionalLight(0xffffff, 0.3);
  rim.position.set(0, -3, -5);
  scene.add(rim);

  /* Orthographic camera — no perspective distortion (better for dental) */
  const aspect = W / H;
  const frustum = 1;
  const camera = new THREE.OrthographicCamera(
    -frustum * aspect, frustum * aspect, frustum, -frustum, 0.1, 10000,
  );
  camera.position.set(0, 1, 5);
  camera.lookAt(0, 0, 0);

  /* Headlight — pinned to the camera every frame (see render loop). This is
     what produces the MeshLab-style N·L gradient that matches the webapp.
     Intensity 1.0 (not the webapp's 2.0): Drive runs three@0.142 with legacy
     light units, so 2.0 here over-exposed the clay to pure white — 1.0 lands the
     brightest highlight on the clay albedo (0xd9d9d3) like the webapp. */
  const headLight = new THREE.DirectionalLight(0xffffff, 1.0);
  headLight.position.copy(camera.position);
  headLight.target.position.set(0, 0, 0);
  scene.add(headLight);
  scene.add(headLight.target);

  /* Controls — turntable drag: OrbitControls keeps the up vector fixed (the
     model stays upright and never rolls). No auto-rotation. */
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping   = true;
  controls.dampingFactor   = 0.06;
  controls.rotateSpeed     = 0.65;
  controls.zoomSpeed       = 1.2;
  controls.screenSpacePanning = true;

  return {renderer, scene, camera, controls, headLight, W, H};
}

/* ── Fit orthographic camera to bounding box ── */
function fitCamera(camera, controls, box) {
  const size   = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z) * 0.6;
  const aspect = camera.right / camera.top;           // current aspect
  camera.left   = -maxDim * aspect;
  camera.right  =  maxDim * aspect;
  camera.top    =  maxDim;
  camera.bottom = -maxDim;
  camera.near   = 0.001;
  camera.far    = maxDim * 20;
  camera.updateProjectionMatrix();
  camera.position.set(0, maxDim * 0.5, maxDim * 2.5);
  camera.lookAt(0, 0, 0);
  controls.target.set(0, 0, 0);
  controls.update();
}

/* ── Load geometry from S3 URL via fetch → ArrayBuffer ── */
async function loadGeometryFromUrl(url, ext, onProgress) {
  onProgress(5);

  /* Fetch with progress */
  const response = await fetch(url);
  const total    = Number(response.headers.get("content-length")) || 0;
  const reader   = response.body.getReader();
  const chunks   = [];
  let received   = 0;

  while (true) {
    const {done, value} = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    if (total) onProgress(5 + Math.round((received / total) * 60));
  }

  const buf = new Uint8Array(received);
  let offset = 0;
  for (const c of chunks) { buf.set(c, offset); offset += c.length; }
  const arrayBuffer = buf.buffer;

  onProgress(70);

  let geometry;
  switch (ext) {
    case "stl":
      geometry = mergeVertices(new STLLoader().parse(arrayBuffer));
      break;

    case "ply":
      geometry = mergeVertices(new PLYLoader().parse(arrayBuffer));
      break;

    case "obj": {
      const text = new TextDecoder().decode(arrayBuffer);
      const group = new OBJLoader().parse(text);
      const firstMesh = group.children.find(c => c.isMesh);
      geometry = firstMesh?.geometry ?? new THREE.BufferGeometry();
      break;
    }

    default:
      geometry = new THREE.BufferGeometry();
  }

  geometry.computeVertexNormals();
  onProgress(90);
  return geometry;
}

/* ═══════════════════════════════════════════════
   ThreeDViewer — main full-screen viewer
═══════════════════════════════════════════════ */
export default function ThreeDViewer({url, filename, onClose}) {
  const canvasRef = useRef(null);
  const sceneRef  = useRef(null);

  const [loading,   setLoading]   = useState(true);
  const [progress,  setProgress]  = useState(0);
  const [error,     setError]     = useState(null);
  const [wireframe, setWireframe] = useState(false);
  const [matKey,    setMatKey]    = useState("tooth");
  const [stats,     setStats]     = useState({vertices: "—", faces: "—"});
  const [camInfo,   setCamInfo]   = useState({center: null, maxDim: null});

  const ext = filename?.split(".").pop()?.toLowerCase() || "stl";

  /* ── Init scene ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !url) return;

    const S = buildScene(canvas);
    sceneRef.current = S;

    /* Resize */
    const ro = new ResizeObserver(() => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      S.renderer.setSize(w, h, false);
      // Update orthographic camera aspect
      const aspect = w / h;
      const halfH = S.camera.top;
      S.camera.left  = -halfH * aspect;
      S.camera.right =  halfH * aspect;
      S.camera.updateProjectionMatrix();
    });
    ro.observe(canvas);

    /* Render loop */
    let rafId;
    const loop = () => {
      rafId = requestAnimationFrame(loop);
      S.controls.update();
      if (S.headLight) S.headLight.position.copy(S.camera.position); // headlight rides the camera
      S.renderer.render(S.scene, S.camera);
    };
    loop();

    /* Load geometry */
    const mat = MAT_PRESETS[matKey].make();
    loadGeometryFromUrl(url, ext, p => setProgress(p))
      .then(geometry => {
        const mesh = new THREE.Mesh(geometry, mat);
        mesh.castShadow = mesh.receiveShadow = true;

        /* Centre at origin */
        const box = new THREE.Box3().setFromObject(mesh);
        mesh.position.sub(box.getCenter(new THREE.Vector3()));
        S.scene.add(mesh);
        S.mesh = mesh;

        /* Recompute box after centring */
        const centredBox = new THREE.Box3().setFromObject(mesh);
        const size    = centredBox.getSize(new THREE.Vector3());
        const maxDim  = Math.max(size.x, size.y, size.z);
        fitCamera(S.camera, S.controls, centredBox);
        setCamInfo({center: new THREE.Vector3(0, 0, 0), maxDim});

        const v = geometry.attributes.position?.count ?? 0;
        setStats({vertices: v.toLocaleString(), faces: Math.round(v / 3).toLocaleString()});
        setProgress(100);
        setLoading(false);
      })
      .catch(err => {
        console.error("[ThreeDViewer]", err);
        setError("Could not load model. Check network access to the file.");
        setLoading(false);
      });

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      if (S.mesh) {
        S.scene.remove(S.mesh);
        S.mesh.traverse?.(c => { c.geometry?.dispose(); Array.isArray(c.material) ? c.material.forEach(m => m.dispose()) : c.material?.dispose?.(); });
      }
      S.renderer.dispose();
    };
  }, [url]);

  /* ── Wireframe toggle ── */
  useEffect(() => {
    const S = sceneRef.current;
    if (!S?.mesh) return;
    const applyWf = obj => { if (obj.isMesh && obj.material) { [].concat(obj.material).forEach(m => { m.wireframe = wireframe; }); } };
    S.mesh.traverse ? S.mesh.traverse(applyWf) : applyWf(S.mesh);
  }, [wireframe]);

  /* ── Material swap ── */
  const applyMaterial = useCallback(key => {
    const S = sceneRef.current;
    if (!S?.mesh) return;
    const newMat = MAT_PRESETS[key].make();
    newMat.wireframe = wireframe;
    const swap = obj => { if (obj.isMesh) obj.material = newMat; };
    S.mesh.traverse ? S.mesh.traverse(swap) : swap(S.mesh);
    setMatKey(key);
  }, [wireframe]);

  /* ── Reset camera ── */
  const resetCamera = useCallback(() => {
    const S = sceneRef.current;
    if (!S?.mesh) return;
    const box = new THREE.Box3().setFromObject(S.mesh);
    fitCamera(S.camera, S.controls, box);
  }, []);

  const badgeColors = {stl: "#a78bfa", obj: "#7c6af7", ply: "#34d399"};
  const bc = badgeColors[ext] || "#8892b0";

  return (
    <div className={styles.shell}>

      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <div className={styles.fileInfo}>
          <span className={styles.fileBadge} style={{color: bc, borderColor: bc + "55", background: bc + "18"}}>
            {ext.toUpperCase()}
          </span>
          <span className={styles.fileName}>{filename}</span>
        </div>

        <div className={styles.controls}>
          <button className={styles.btn} onClick={resetCamera}>⟳ Reset</button>
          <button className={`${styles.btn} ${wireframe ? styles.btnActive : ""}`} onClick={() => setWireframe(v => !v)}>
            ⊞ Wireframe
          </button>
          <button className={`${styles.btn} ${styles.btnClose}`} onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {/* ── Canvas ── */}
      <canvas ref={canvasRef} className={styles.canvas} />

      {/* Material picker removed — single clay material, same as the swebapp
          STLFullViewer (the New Documentation 3D viewer). */}

      {/* ── Loading overlay ── */}
      {loading && !error && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C9.5 2 7.5 3.5 6 5.5C4.5 7.5 4 10 4 12C4 15 5 18 6.5 20.5C7.5 22 9 22 10 21C10.5 20.5 11 19 12 19C13 19 13.5 20.5 14 21C15 22 16.5 22 17.5 20.5C19 18 20 15 20 12C20 10 19.5 7.5 18 5.5C16.5 3.5 14.5 2 12 2Z" fill="#a78bfa"/>
            </svg>
          </div>
          <div className={styles.loadingTitle}>Loading {ext.toUpperCase()} model…</div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{width: `${progress}%`}} />
          </div>
          <div className={styles.loadingPct}>{progress}%</div>
        </div>
      )}

      {/* ── Error overlay ── */}
      {error && (
        <div className={styles.errorOverlay}>
          <div className={styles.errorTitle}>Could not load model</div>
          <div className={styles.errorSub}>{error}</div>
          <button className={`${styles.btn} ${styles.btnClose}`} onClick={onClose} style={{marginTop: 16}}>✕ Close</button>
        </div>
      )}

      {/* ── Stats bar ── */}
      {!loading && !error && (
        <div className={styles.statsBar}>
          <div className={styles.statItem}><div className={styles.statDot}/>{stats.vertices} vertices</div>
          <div className={styles.statItem}><div className={styles.statDot} style={{background: "#e85878"}}/>{stats.faces} faces</div>
          <span className={styles.hint}>Drag to rotate · Scroll to zoom · Right-drag to pan</span>
        </div>
      )}
    </div>
  );
}
